import { Grid, GridItem, RadioField, RadioInput } from '@flos/react-ui';
import {
    CancellationReason,
    isAgricultureLob,
    isModifiableCancellationReason,
    shouldHideEdiChooseStatus,
    updateExternalCaseDTOProperties
} from '../../../utils/externalCasesUtil';
import {
    EdiCaseStatusMap,
    type LobStatusFieldsValidation
} from '../steps/DatesAndCancellationStep';
import StringParagraph from './StringParagraph';
import { useRecoilState, useRecoilValue } from 'recoil';
import { externalCasesAtom } from '../../../recoil/atoms/externalCasesAtom';
import type { ExternaleCaseDTO } from 'gw-edge-client/dist/types/dto/pc/top/edge/capabilities/policyjob/edi/dto/';
import type { ExternalCaseStatus_Top } from 'gw-edge-client/dist/types/dto/pc/typekey/';
import { useHippoText } from '../../../hooks/useHippoText';
import { accountHolderAtom } from '../../../recoil/atoms/accountHolderAtom';
import { useState } from 'react';

export const EDICancellationChooseStatus = ({
    externalCase,
    flavourName,
    coverableName,
    lobStatusFieldValidation
}: {
    externalCase: ExternaleCaseDTO;
    flavourName: string;
    coverableName?: string;
    lobStatusFieldValidation: LobStatusFieldsValidation;
}): React.JSX.Element => {
    const getHippoText = useHippoText();
    const [externalCaseDTOList, setExternalCaseDTOList] = useRecoilState(externalCasesAtom);
    const accountHolder = useRecoilValue(accountHolderAtom);
    const { caseId, status, lobCode } = externalCase;
    const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
    const handleCancellationChoice = (caseId: string, statusId: string): void => {
        const status = statusId as ExternalCaseStatus_Top;
        const cancellationReason =
            accountHolder.isCVR &&
            (status === EdiCaseStatusMap.ReadyForSend || EdiCaseStatusMap.PendingCompletion)
                ? CancellationReason.ExpirationDate
                : undefined;
        const updatedExternalCaseProperties = updateExternalCaseDTOProperties(
            externalCaseDTOList,
            [caseId],
            {
                status,
                cancellationDate: undefined,
                startDate: undefined,
                otherCompany: undefined,
                cancellationReason,
                cancellationOnExpirationDate: undefined,
                insuredIsAccountHolder: statusId !== EdiCaseStatusMap.NotRequired ? true : undefined
            }
        );
        setExternalCaseDTOList(updatedExternalCaseProperties);
        setHasUnsavedChanges(true);
    };

    if (!caseId || shouldHideEdiChooseStatus(lobCode as string)) return <></>;

    const getEdiCaseSubtitle = (): string => {
        if (flavourName === 'Indboforsikring til unge') {
            return getHippoText('DSS-EFFECTIVE-DATE-AND-CANCELLATION-INSURED-SUBHEADER', {
                insurance: 'indboforsikring'
            });
        } else if (flavourName === 'Ulykkesforsikring til unge') {
            return getHippoText('DSS-EFFECTIVE-DATE-AND-CANCELLATION-INSURED-SUBHEADER', {
                insurance: 'ulykkesforsikring'
            });
        } else if (
            coverableName !== undefined &&
            (flavourName.toLocaleLowerCase() === 'husforsikring' ||
                flavourName.toLocaleLowerCase() === 'fritidshusforsikring')
        ) {
            return getHippoText('DSS-EFFECTIVE-DATE-AND-CANCELLATION-INSURED-SUBHEADER-PRH', {
                adresse: coverableName
            });
        }
        return getHippoText(
            accountHolder.isCVR
                ? 'DSS-EFFECTIVE-DATE-AND-CANCELLATION-CVR-INSURED-SUBHEADER'
                : 'DSS-EFFECTIVE-DATE-AND-CANCELLATION-INSURED-SUBHEADER',
            {
                insurance: String(flavourName)?.toLowerCase()
            }
        );
    };

    if (!caseId || shouldHideEdiChooseStatus(lobCode as string)) return <></>;

    return (
        <Grid className=" u-spacing-stack-top-5" gap={6}>
            <GridItem span={8}>
                <StringParagraph>{getEdiCaseSubtitle()}</StringParagraph>
            </GridItem>
            <GridItem span={4}>
                <div id={`cancellationChoiceBtnGroup-${caseId}`}>
                    <RadioField
                        value={String(status)}
                        onChange={(e) => {
                            handleCancellationChoice(caseId, e.currentTarget.value);
                        }}
                        variant="button-toolbar"
                        stacked={false}
                        disabled={
                            !isModifiableCancellationReason(
                                externalCase.cancellationReason,
                                flavourName
                            )
                        }
                        isValid={
                            lobStatusFieldValidation?.isStatusValid === undefined ||
                            lobStatusFieldValidation.isStatusValid ||
                            hasUnsavedChanges
                        }
                    >
                        <RadioInput
                            value={
                                lobCode && isAgricultureLob(lobCode)
                                    ? String(EdiCaseStatusMap.PendingCompletion)
                                    : String(EdiCaseStatusMap.ReadyForSend)
                            }
                            label={getHippoText('DSS-EFFECTIVE-DATE-AND-CANCELLATION-BUTTON-YES')}
                            defaultChecked={status === EdiCaseStatusMap.ReadyForSend}
                        />
                        <RadioInput
                            value={String(EdiCaseStatusMap.NotRequired)}
                            label={getHippoText('DSS-EFFECTIVE-DATE-AND-CANCELLATION-BUTTON-NO')}
                        />
                    </RadioField>
                </div>
            </GridItem>
        </Grid>
    );
};
